import ActionSuccessToast from '../components/ActionSuccessToast'
import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Moon, Sun, Trash2, LogOut, ExternalLink, RefreshCw, AlertCircle, Fingerprint, Plus, X, Plug, CheckCircle, XCircle, Eye, EyeOff, Palette, RotateCcw, Save, Pencil, Clock, Server } from 'lucide-react'
import { startRegistration } from '@simplewebauthn/browser'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useAutoSyncContext } from '../hooks/useAutoSync'
import { useSettings } from '../hooks/useSettings'
import { useMode } from '../hooks/useMode'
import { useBiometricGate } from '../hooks/useBiometricGate'
import { useBrokerConnection } from '../hooks/useBrokerConnection'
import { usePrivacy } from '../hooks/usePrivacy'
import { useNavbarClockPreferences } from '../hooks/useNavbarClockPreferences'
import { preserveResizableWorkspaceColumnWidths } from '../hooks/useResizableWorkspaceColumns'
import {
  THEME_BUILDER_TOKENS,
  applyThemeBuilderColors,
  clearSelectedThemePalette,
  deleteThemePalette,
  getSelectedThemePaletteId,
  getThemeBuilderValues,
  getThemePalettes,
  loadThemePalette,
  normalizeHex,
  resetThemeBuilderMode,
  saveThemePalette,
  setThemeBuilderColor,
} from '../lib/themeBuilder'
import {
  hydrateThemeBuilderStorageFromServer,
  persistThemeBuilderStorage,
} from '../lib/themePreferenceStore'
import {
  NAVBAR_CLOCK_FONT_OPTIONS,
  NAVBAR_CLOCK_SIZE_OPTIONS,
  normalizeNavbarClockColor,
} from '../lib/navbarClockPreferences'
import AccountName from '../components/AccountName'
import PrivateValue from '../components/PrivateValue'
import CommandSettings from '../components/commands/CommandSettings'
import MarketDataOperations from '../components/settings/MarketDataOperations'

function fmtEq(val) {
  if (val == null) return '—'
  const n = Number(val)
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${n.toFixed(2)}`
}

const SETTINGS_AREAS = [
  {
    id: 'account',
    label: 'Account & Security',
    description: 'Password, passkeys, and local data',
    icon: Fingerprint,
  },
  {
    id: 'broker',
    label: 'Broker & Data',
    description: 'Broker connection and account synchronization',
    icon: Plug,
  },
  {
    id: 'market-data',
    label: 'Market Data Operations',
    description: 'IBKR health and recovery controls',
    icon: Server,
  },
  {
    id: 'trading',
    label: 'Trading & Risk',
    description: 'Sizing defaults, OMS, and execution limits',
    icon: AlertCircle,
  },
  {
    id: 'workspace',
    label: 'Workspace',
    description: 'Navigation, timezone, and working mode',
    icon: ExternalLink,
  },
  {
    id: 'appearance',
    label: 'Look & Feel',
    description: 'Theme, privacy, and visual preferences',
    icon: Palette,
  },
]

const CLOCK_COLOR_MODES = [
  { id: 'light', label: 'Light Mode', ariaPrefix: 'Light' },
  { id: 'dark', label: 'Dark Mode', ariaPrefix: 'Dark' },
]

export default function SettingsPage() {
  const { user, signOut } = useAuth()
  const { settings, loading: settingsLoading, updateSettings } = useSettings()
  const { triggerSync } = useAutoSyncContext()
  const biometricGate = useBiometricGate()
  const { privacyMode, togglePrivacy } = usePrivacy()
  const {
    preferences: clockPreferences,
    updatePreferences: updateClockPreferences,
    resetPreferences: resetClockPreferences,
  } = useNavbarClockPreferences()
  const [clockColorDrafts, setClockColorDrafts] = useState(() => ({
    light: clockPreferences.colors.light || '',
    dark: clockPreferences.colors.dark || '',
  }))
  const [clockAppearanceMsg, setClockAppearanceMsg] = useState(null)
  // BUG-OMS-LIVE-DAYCHANGE-2026-05-21 Fix 1: the Section 3B "LIVE" badge must
  // reflect broker connection health, not just the user's intent. Mirrors the
  // schwabAuthExpired derivation in useOrderGateway so badge and gateway agree.
  const brokerConn = useBrokerConnection('schwab')
  const schwabAuthExpired = brokerConn.requiresReauth || brokerConn.reason === 'no_connection'
  const omsLive = (settings?.oms_enabled || false) && !schwabAuthExpired
  const loginWebAuthnEnabled = settings?.login_webauthn_enabled ?? false
  const [searchParams, setSearchParams] = useSearchParams()
  const { mode, setModeWithGate } = useMode()
  const [modeDbWriting, setModeDbWriting] = useState(false)
  const [modeMsg, setModeMsg] = useState(null)
  const modeBusy = biometricGate.verifying || modeDbWriting
  const handleModeChange = async (next) => {
    if (next === mode || modeBusy) return
    setModeMsg(null)
    setModeDbWriting(true)
    try {
      const result = await setModeWithGate(next)
      if (!result.committed && result.error && result.error !== 'no-op') {
        setModeMsg({ type: 'error', text: result.error })
      }
    } finally {
      setModeDbWriting(false)
    }
  }

  // FA-01-passkey-hardening (D2): surface the specific biometric-gate
  // error when the OMS toggle blocks (e.g. "Passkey required. Set one
  // up in Settings before sending orders.").
  const [omsError, setOmsError] = useState(null)

  // ── Password change ───────────────────────────────────────────────────
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwMsg, setPwMsg] = useState(null)

  const handleChangePassword = async () => {
    setPwMsg(null)
    if (newPassword.length < 6) {
      setPwMsg({ type: 'error', text: 'Password must be at least 6 characters' })
      return
    }
    if (newPassword !== confirmPassword) {
      setPwMsg({ type: 'error', text: 'Passwords do not match' })
      return
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) {
      setPwMsg({ type: 'error', text: error.message })
    } else {
      setPwMsg({ type: 'success', text: 'Password updated successfully' })
      setNewPassword('')
      setConfirmPassword('')
    }
  }

  // ── Dark mode ─────────────────────────────────────────────────────────
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('bwc-dark-mode')
    return saved !== null ? saved === 'true' : true
  })
  useEffect(() => {
    const syncDarkMode = () => {
      const saved = localStorage.getItem('bwc-dark-mode')
      setDark(saved !== null ? saved === 'true' : true)
    }

    window.addEventListener('bwc-theme-change', syncDarkMode)
    window.addEventListener('storage', syncDarkMode)
    return () => {
      window.removeEventListener('bwc-theme-change', syncDarkMode)
      window.removeEventListener('storage', syncDarkMode)
    }
  }, [])
  const activeThemeMode = dark ? 'dark' : 'light'
  const activeThemeLabel = dark ? 'Dark' : 'Light'
  const [themeBuilderColors, setThemeBuilderColors] = useState(() => getThemeBuilderValues(activeThemeMode))
  const [themeBuilderDrafts, setThemeBuilderDrafts] = useState(() => getThemeBuilderValues(activeThemeMode))
  const [themePalettes, setThemePalettes] = useState(() => getThemePalettes(activeThemeMode))
  const [selectedThemePaletteId, setSelectedThemePaletteId] = useState(() => getSelectedThemePaletteId(activeThemeMode))
  const [themePaletteName, setThemePaletteName] = useState(() => {
    const selectedId = getSelectedThemePaletteId(activeThemeMode)
    return getThemePalettes(activeThemeMode).find(palette => palette.id === selectedId)?.name || ''
  })
  const [themeBuilderMsg, setThemeBuilderMsg] = useState(null)
  const [themeBuilderOpen, setThemeBuilderOpen] = useState(false)
  const [editingThemePaletteId, setEditingThemePaletteId] = useState(null)
  const [themeSyncing, setThemeSyncing] = useState(false)

  function syncThemeBuilderState(nextValues = getThemeBuilderValues(activeThemeMode)) {
    const nextPalettes = getThemePalettes(activeThemeMode)
    const nextSelectedId = getSelectedThemePaletteId(activeThemeMode)
    setThemeBuilderColors(nextValues)
    setThemeBuilderDrafts(nextValues)
    setThemePalettes(nextPalettes)
    setSelectedThemePaletteId(nextSelectedId)
    setThemePaletteName(nextPalettes.find(palette => palette.id === nextSelectedId)?.name || '')
  }

  async function persistThemeBuilderPreferences(successText) {
    if (!user?.id) {
      setThemeBuilderMsg({ type: 'error', text: 'Sign in required to sync themes' })
      return false
    }

    setThemeSyncing(true)
    try {
      const ok = await persistThemeBuilderStorage({ userId: user.id })
      setThemeBuilderMsg(ok
        ? { type: 'success', text: successText }
        : { type: 'error', text: 'Saved locally, but Supabase sync failed' })
      return ok
    } finally {
      setThemeSyncing(false)
    }
  }

  useEffect(() => {
    const nextValues = getThemeBuilderValues(activeThemeMode)
    document.documentElement.classList.toggle('dark', dark)
    applyThemeBuilderColors(activeThemeMode)
    syncThemeBuilderState(nextValues)
    setThemeBuilderMsg(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThemeMode, dark])

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    setThemeSyncing(true)
    hydrateThemeBuilderStorageFromServer({ userId: user.id })
      .then(() => {
        if (cancelled) return
        applyThemeBuilderColors(activeThemeMode)
        syncThemeBuilderState(getThemeBuilderValues(activeThemeMode))
      })
      .catch(() => {
        if (!cancelled) setThemeBuilderMsg({ type: 'error', text: 'Theme sync unavailable' })
      })
      .finally(() => {
        if (!cancelled) setThemeSyncing(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const toggleDark = () => {
    setDark(d => {
      const next = !d
      localStorage.setItem('bwc-dark-mode', String(next))
      document.documentElement.classList.toggle('dark', next)
      applyThemeBuilderColors(next ? 'dark' : 'light')
      window.dispatchEvent(new Event('bwc-theme-change'))
      return next
    })
  }

  useEffect(() => {
    setClockColorDrafts({
      light: clockPreferences.colors.light || '',
      dark: clockPreferences.colors.dark || '',
    })
  }, [clockPreferences.colors.light, clockPreferences.colors.dark])

  const handleClockPreference = (key, value) => {
    setClockAppearanceMsg(null)
    updateClockPreferences({ [key]: value })
  }

  const updateClockColor = (mode, color) => {
    updateClockPreferences(current => ({
      colors: {
        ...current.colors,
        [mode]: color,
      },
    }))
  }

  const handleClockColor = (mode, rawValue) => {
    const normalized = normalizeNavbarClockColor(rawValue)
    if (!normalized) return
    setClockColorDrafts(current => ({ ...current, [mode]: normalized }))
    setClockAppearanceMsg(null)
    updateClockColor(mode, normalized)
  }

  const handleClockColorDraft = (mode, rawValue) => {
    setClockColorDrafts(current => ({ ...current, [mode]: rawValue }))
    const normalized = normalizeNavbarClockColor(rawValue)
    if (!normalized) return
    setClockAppearanceMsg(null)
    updateClockColor(mode, normalized)
  }

  const handleClockColorBlur = mode => {
    if (clockColorDrafts[mode].trim() === '') {
      setClockAppearanceMsg(null)
      updateClockColor(mode, null)
      return
    }
    if (normalizeNavbarClockColor(clockColorDrafts[mode])) return

    setClockColorDrafts(current => ({
      ...current,
      [mode]: clockPreferences.colors[mode] || '',
    }))
    setClockAppearanceMsg({ type: 'error', text: 'Use a six-digit hex color' })
  }

  const handleClockAppearanceReset = () => {
    resetClockPreferences()
    setClockColorDrafts({ light: '', dark: '' })
    setClockAppearanceMsg({ type: 'success', text: 'Clock appearance reset' })
  }

  const handleThemeBuilderColor = (tokenKey, rawValue) => {
    const normalized = normalizeHex(rawValue)
    if (!normalized) {
      setThemeBuilderMsg({ type: 'error', text: 'Use a six-digit hex color' })
      return
    }

    const currentName = themePaletteName
    const currentEditingId = editingThemePaletteId
    setThemeBuilderMsg(null)
    const nextValues = setThemeBuilderColor(activeThemeMode, tokenKey, normalized)
    syncThemeBuilderState(nextValues)
    if (themeBuilderOpen) {
      setThemePaletteName(currentName)
      setEditingThemePaletteId(currentEditingId)
    }
  }

  const handleThemeBuilderHexInput = (tokenKey, rawValue) => {
    setThemeBuilderDrafts(prev => ({ ...prev, [tokenKey]: rawValue }))
    const normalized = normalizeHex(rawValue)
    if (!normalized) return

    const currentName = themePaletteName
    const currentEditingId = editingThemePaletteId
    setThemeBuilderMsg(null)
    const nextValues = setThemeBuilderColor(activeThemeMode, tokenKey, normalized)
    syncThemeBuilderState(nextValues)
    if (themeBuilderOpen) {
      setThemePaletteName(currentName)
      setEditingThemePaletteId(currentEditingId)
    }
  }

  const handleThemeBuilderHexBlur = (tokenKey) => {
    const rawValue = themeBuilderDrafts[tokenKey]
    if (normalizeHex(rawValue)) return

    setThemeBuilderDrafts(prev => ({ ...prev, [tokenKey]: themeBuilderColors[tokenKey] }))
    setThemeBuilderMsg({ type: 'error', text: 'Use a six-digit hex color' })
  }

  const handleThemeBuilderReset = async () => {
    const nextValues = resetThemeBuilderMode(activeThemeMode)
    syncThemeBuilderState(nextValues)
    setThemeBuilderOpen(false)
    setEditingThemePaletteId(null)
    await persistThemeBuilderPreferences(`${activeThemeLabel} theme reset`)
  }

  const handleThemePaletteCreate = () => {
    setEditingThemePaletteId(null)
    setThemePaletteName('')
    setThemeBuilderDrafts(themeBuilderColors)
    setThemeBuilderOpen(true)
    setThemeBuilderMsg(null)
  }

  const handleThemePaletteEdit = (paletteId) => {
    const result = loadThemePalette(activeThemeMode, paletteId)
    if (result.error) {
      setThemeBuilderMsg({ type: 'error', text: result.error })
      return
    }

    syncThemeBuilderState(result.values)
    setEditingThemePaletteId(paletteId)
    setThemePaletteName(result.palette.name)
    setThemeBuilderOpen(true)
    setThemeBuilderMsg(null)
  }

  const handleThemePaletteSave = async () => {
    const result = saveThemePalette(activeThemeMode, themePaletteName, themeBuilderColors, {
      paletteId: editingThemePaletteId,
    })
    if (result.error) {
      setThemeBuilderMsg({ type: 'error', text: result.error })
      return
    }

    syncThemeBuilderState(result.values)
    setThemePaletteName(result.palette.name)
    setEditingThemePaletteId(result.palette.id)
    setThemeBuilderOpen(false)
    await persistThemeBuilderPreferences(`Saved ${result.palette.name}`)
  }

  const handleThemePaletteLoad = async (paletteId) => {
    if (!paletteId) {
      const result = clearSelectedThemePalette(activeThemeMode)
      syncThemeBuilderState(result.values)
      setThemeBuilderOpen(false)
      setEditingThemePaletteId(null)
      await persistThemeBuilderPreferences('Using custom theme')
      return
    }

    const result = loadThemePalette(activeThemeMode, paletteId)
    if (result.error) {
      setThemeBuilderMsg({ type: 'error', text: result.error })
      return
    }

    syncThemeBuilderState(result.values)
    setThemePaletteName(result.palette.name)
    setThemeBuilderOpen(false)
    setEditingThemePaletteId(null)
    await persistThemeBuilderPreferences(`Loaded ${result.palette.name}`)
  }

  const handleThemePaletteDelete = async (paletteId) => {
    const palette = themePalettes.find(item => item.id === paletteId)
    if (!palette) {
      setThemeBuilderMsg({ type: 'error', text: 'Theme not found' })
      return
    }
    if (!confirm(`Delete theme "${palette.name}"?`)) return

    const result = deleteThemePalette(activeThemeMode, paletteId)
    if (result.error) {
      setThemeBuilderMsg({ type: 'error', text: result.error })
      return
    }

    syncThemeBuilderState(result.values)
    if (editingThemePaletteId === paletteId) {
      setThemeBuilderOpen(false)
      setEditingThemePaletteId(null)
    }
    await persistThemeBuilderPreferences(`Deleted ${palette.name}`)
  }

  // ── Trading defaults ──────────────────────────────────────────────────
  const [allocPct, setAllocPct] = useState(9)
  const [riskPct, setRiskPct] = useState(7.5)
  const [tranches, setTranches] = useState(3)
  const [settingsMsg, setSettingsMsg] = useState(null)

  useEffect(() => {
    if (!settingsLoading && settings) {
      setAllocPct(settings.default_alloc_pct ?? 9)
      setRiskPct(settings.default_risk_pct ?? 7.5)
      setTranches(settings.default_tranches ?? 3)
    }
  }, [settingsLoading, settings])

  const handleSaveDefaults = async () => {
    setSettingsMsg(null)
    const { error } = await updateSettings({
      default_alloc_pct: Number(allocPct),
      default_risk_pct: Number(riskPct),
      default_tranches: Number(tranches),
    })
    if (error) {
      setSettingsMsg({ type: 'error', text: error.message })
    } else {
      setSettingsMsg({ type: 'success', text: 'Trading defaults saved' })
    }
  }

  // ── OMS Safety Limits ──────────────────────────────────────────────────
  const [maxQty, setMaxQty] = useState(500)
  const [maxNotional, setMaxNotional] = useState(10000)
  const [limitsMsg, setLimitsMsg] = useState(null)

  useEffect(() => {
    if (!settingsLoading && settings) {
      setMaxQty(settings.oms_max_quantity ?? 500)
      setMaxNotional(settings.oms_max_notional ?? 10000)
    }
  }, [settingsLoading, settings])

  const handleSaveLimits = async () => {
    setLimitsMsg(null)
    const { verified, error: bioError } = await biometricGate.verify()
    if (!verified) {
      setLimitsMsg({ type: 'error', text: bioError || 'Touch ID required to change safety limits' })
      return
    }
    const qty = Number(maxQty)
    const notional = Number(maxNotional)
    if (!qty || qty < 1 || qty > 10000) {
      setLimitsMsg({ type: 'error', text: 'Max quantity must be between 1 and 10,000' })
      return
    }
    if (!notional || notional < 100 || notional > 1000000) {
      setLimitsMsg({ type: 'error', text: 'Max notional must be between $100 and $1,000,000' })
      return
    }
    const { error } = await updateSettings({ oms_max_quantity: qty, oms_max_notional: notional })
    if (error) {
      setLimitsMsg({ type: 'error', text: error.message })
    } else {
      setLimitsMsg({ type: 'success', text: 'Safety limits updated' })
    }
  }

  // ── Integrations + Data & Sync ────────────────────────────────────────
  const [tokenRow, setTokenRow] = useState(null)
  const [syncLoading, setSyncLoading] = useState(true)
  const [accounts, setAccounts] = useState([])
  const [positionCounts, setPositionCounts] = useState({})
  const [integrationsSyncing, setIntegrationsSyncing] = useState(false)
  const [integrationsBanner, setIntegrationsBanner] = useState(null)

  useEffect(() => {
    const connected = searchParams.get('connected')
    const err = searchParams.get('error')
    if (connected === 'true') {
      setIntegrationsBanner({ type: 'success', message: 'Schwab account connected successfully.' })
      setSearchParams({}, { replace: true })
      // Recalculates tokenWarning from the fresh broker_tokens.created_at
      // so the navbar banner clears immediately instead of on the next
      // 15-minute auto-sync cycle.
      triggerSync()
    } else if (err) {
      setIntegrationsBanner({ type: 'error', message: decodeURIComponent(err) })
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadSyncData = useCallback(async () => {
    if (!user) return
    const [{ data: token }, { data: accts }, { data: pos }] = await Promise.all([
      supabase
        .from('broker_tokens')
        .select('broker, expires_at, updated_at, created_at')
        .eq('user_id', user.id)
        .eq('broker', 'schwab')
        .maybeSingle(),
      supabase
        .from('schwab_accounts')
        .select('account_number, nick_name, account_type, equity, synced_at')
        .eq('user_id', user.id)
        .order('account_number'),
      supabase
        .from('schwab_positions')
        .select('account_number')
        .eq('user_id', user.id),
    ])
    setTokenRow(token)
    setAccounts(accts || [])
    const counts = {}
    for (const p of (pos || [])) {
      counts[p.account_number] = (counts[p.account_number] || 0) + 1
    }
    setPositionCounts(counts)
    setSyncLoading(false)
  }, [user])

  const handleConnect = () => { window.location.href = '/api/schwab/auth?action=login' }
  const handleDisconnect = async () => {
    if (!confirm('Disconnect Schwab? Your synced data will be preserved.')) return
    await supabase.from('broker_tokens').delete().eq('user_id', user.id).eq('broker', 'schwab')
    setTokenRow(null)
    setIntegrationsBanner({ type: 'error', message: 'Schwab disconnected.' })
  }
  const handleSyncNow = async () => {
    setIntegrationsSyncing(true)
    setIntegrationsBanner(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/schwab/sync', { headers: { Authorization: `Bearer ${session.access_token}` } })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Sync failed')
      const { summary } = json
      const errNote = summary.errors.length > 0 ? ` (${summary.errors.length} warning${summary.errors.length > 1 ? 's' : ''})` : ''
      setIntegrationsBanner({
        type: summary.errors.length > 0 ? 'warning' : 'success',
        message: `Synced ${summary.accounts} account${summary.accounts !== 1 ? 's' : ''}, ${summary.positions} position${summary.positions !== 1 ? 's' : ''}, ${summary.transactions} transaction${summary.transactions !== 1 ? 's' : ''}${errNote}.`,
      })
      await loadSyncData()
    } catch (err) {
      setIntegrationsBanner({ type: 'error', message: err.message })
    } finally {
      setIntegrationsSyncing(false)
    }
  }

  useEffect(() => { loadSyncData() }, [loadSyncData])

  const tokenAge = (() => {
    if (!tokenRow?.created_at) return null
    const authDate = new Date(tokenRow.created_at)
    const now = new Date()
    const daysElapsed = (now - authDate) / (1000 * 60 * 60 * 24)
    const daysRemaining = Math.max(0, 7 - daysElapsed)
    return {
      authDate: authDate.toLocaleDateString(),
      lastSync: tokenRow.updated_at ? new Date(tokenRow.updated_at).toLocaleString() : null,
      daysRemaining: Math.ceil(daysRemaining),
      isExpired: daysRemaining <= 0,
      isWarning: daysRemaining > 0 && daysRemaining <= 2,
    }
  })()

  // ── Danger zone ───────────────────────────────────────────────────────
  const handleClearLocal = () => {
    if (!confirm('Clear all local preferences? This will reset dark mode and other localStorage settings.')) return
    preserveResizableWorkspaceColumnWidths(() => localStorage.clear())
    window.location.reload()
  }

  const handleSignOut = async () => {
    await signOut()
  }

  // ── Passkeys ──────────────────────────────────────────────────────────
  const [passkeys, setPasskeys] = useState([])
  const [passkeysLoading, setPasskeysLoading] = useState(true)
  const [passkeyMsg, setPasskeyMsg] = useState(null)
  const [loginAuthMsg, setLoginAuthMsg] = useState(null)
  const [registerLoading, setRegisterLoading] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null) // passkey id pending delete

  const loadPasskeys = useCallback(async () => {
    if (!user) return
    setPasskeysLoading(true)
    const { data } = await supabase
      .from('user_passkeys')
      .select('id, device_name, created_at, last_used_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
    setPasskeys(data || [])
    setPasskeysLoading(false)
  }, [user])

  useEffect(() => { loadPasskeys() }, [loadPasskeys])

  const handleRegisterPasskey = async () => {
    setPasskeyMsg(null)
    setRegisterLoading(true)
    try {
      const session = (await supabase.auth.getSession()).data.session
      if (!session) { setPasskeyMsg({ type: 'error', text: 'Not authenticated' }); return }

      // Get registration options
      const optRes = await fetch('/api/auth/passkey-register-options', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      const options = await optRes.json()
      if (!optRes.ok) { setPasskeyMsg({ type: 'error', text: options.error || 'Failed to start registration' }); return }

      // Trigger Touch ID / Face ID prompt
      let regResponse
      try {
        regResponse = await startRegistration({ optionsJSON: options })
      } catch (err) {
        if (err.name === 'NotAllowedError') { setPasskeyMsg({ type: 'error', text: 'Touch ID was cancelled' }); return }
        setPasskeyMsg({ type: 'error', text: err.message || 'Registration failed' }); return
      }

      // Build a sensible default device name
      const ua = navigator.userAgent
      let defaultName = 'My Device'
      if (/iPhone/.test(ua)) defaultName = 'iPhone'
      else if (/iPad/.test(ua)) defaultName = 'iPad'
      else if (/Macintosh/.test(ua)) defaultName = 'MacBook'
      else if (/Windows/.test(ua)) defaultName = 'Windows PC'
      else if (/Android/.test(ua)) defaultName = 'Android Phone'
      const deviceName = window.prompt('Name this passkey:', defaultName) || defaultName

      // Verify with backend
      const verifyRes = await fetch('/api/auth/passkey-register-verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ response: regResponse, deviceName }),
      })
      const verifyData = await verifyRes.json()
      if (!verifyRes.ok) { setPasskeyMsg({ type: 'error', text: verifyData.error || 'Registration failed' }); return }

      setPasskeyMsg({ type: 'success', text: `Passkey "${verifyData.deviceName || deviceName}" registered successfully` })
      await loadPasskeys()
    } finally {
      setRegisterLoading(false)
    }
  }

  const handleDeletePasskey = async (id) => {
    if (deleteConfirm !== id) { setDeleteConfirm(id); return }
    const { error } = await supabase.from('user_passkeys').delete().eq('id', id)
    if (error) {
      setPasskeyMsg({ type: 'error', text: error.message })
    } else {
      setPasskeyMsg({ type: 'success', text: 'Passkey removed' })
      await loadPasskeys()
    }
    setDeleteConfirm(null)
  }

  const handleToggleLoginWebAuthn = async () => {
    setLoginAuthMsg(null)
    const next = !loginWebAuthnEnabled
    const { error } = await updateSettings({ login_webauthn_enabled: next })
    if (error) {
      setLoginAuthMsg({ type: 'error', text: error.message })
    } else {
      setLoginAuthMsg({ type: 'success', text: next ? 'WebAuthn login enabled' : 'WebAuthn login disabled' })
    }
  }

  // ── Render helpers ────────────────────────────────────────────────────
  const inputCls = 'w-full bg-canvas2 border border-border rounded-md px-2.5 py-1.5 font-mono text-xs text-ink placeholder:text-inkMuted focus:outline-none focus:border-ink/40 transition-colors'
  const [activeSettingsArea, setActiveSettingsArea] = useState(() => {
    const requested = searchParams.get('area')
    return SETTINGS_AREAS.some(area => area.id === requested) ? requested : 'appearance'
  })

  useEffect(() => {
    const requested = searchParams.get('area')
    if (SETTINGS_AREAS.some(area => area.id === requested)) setActiveSettingsArea(requested)
  }, [searchParams])

  return (
    <div className="max-w-6xl">
      <div className="mb-4">
        <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-0.5">Account</div>
        <h1 className="font-black text-xl uppercase tracking-wide text-ink">Settings</h1>
      </div>

      <div className="lg:grid lg:grid-cols-[12rem_minmax(0,1fr)] lg:gap-6">
        <nav aria-label="Settings areas" className="mb-4 lg:mb-0">
          <div className="flex gap-1.5 overflow-x-auto pb-1 lg:sticky lg:top-4 lg:block lg:space-y-1 lg:overflow-visible lg:rounded-lg lg:border lg:border-border lg:bg-canvas lg:p-1.5">
            {SETTINGS_AREAS.map(area => {
              const Icon = area.icon
              const active = area.id === activeSettingsArea
              return (
                <button
                  key={area.id}
                  type="button"
                  onClick={() => setActiveSettingsArea(area.id)}
                  aria-current={active ? 'page' : undefined}
                  className={`flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-xs font-semibold transition-colors lg:w-full ${
                    active
                      ? 'bg-canvas2 text-ink shadow-sm ring-1 ring-border'
                      : 'text-inkMuted hover:bg-canvas2/60 hover:text-ink'
                  }`}
                >
                  <Icon size={14} className={active ? 'text-ink' : 'text-inkMuted'} />
                  <span>{area.label}</span>
                </button>
              )
            })}
          </div>
        </nav>

        <main className="min-w-0">
          <div className="space-y-4">

      {/* ── Section 1: Appearance ─────────────────────────────────────── */}
      {activeSettingsArea === 'market-data' && <MarketDataOperations />}

      {activeSettingsArea === 'appearance' && (
        <>
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Appearance</h2>
        </div>
        <div className="px-4 py-3 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-ink">Theme</div>
              <div className="font-mono text-xs text-inkMuted mt-0.5">
                {dark ? 'Dark mode is active' : 'Light mode is active'}
              </div>
            </div>
            <button
              onClick={toggleDark}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-canvas2 text-sm font-semibold text-ink hover:border-ink/30 transition-colors"
            >
              {dark ? <Sun size={14} /> : <Moon size={14} />}
              {dark ? 'Switch to Light' : 'Switch to Dark'}
            </button>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-canvas2/40 px-3 py-3">
            <div>
              <div className="text-sm font-semibold text-ink">Privacy Mode</div>
              <div className="font-mono text-xs text-inkMuted mt-0.5">
                {privacyMode ? 'Dollar values are hidden' : 'Dollar values are visible'}
              </div>
            </div>
            <button
              onClick={togglePrivacy}
              aria-pressed={privacyMode}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-canvas text-sm font-semibold text-ink hover:border-ink/30 transition-colors"
            >
              {privacyMode ? <EyeOff size={14} /> : <Eye size={14} />}
              {privacyMode ? 'Show Values' : 'Hide Values'}
            </button>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <Clock size={15} className="text-inkMuted" />
                <div>
                  <div className="text-sm font-semibold text-ink">Navbar Clock</div>
                  <div className="font-mono text-xs text-inkMuted mt-0.5">
                    Browser-local display preferences
                  </div>
                </div>
              </div>
              <button
                type="button"
                aria-label="Reset clock appearance"
                onClick={handleClockAppearanceReset}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-canvas2 px-3 py-2 text-xs font-semibold text-ink hover:border-ink/30 transition-colors"
              >
                <RotateCcw size={13} />
                Reset
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">Size</span>
                <select
                  aria-label="Clock size"
                  value={clockPreferences.size}
                  onChange={event => handleClockPreference('size', event.target.value)}
                  className="mt-1.5 w-full rounded-md border border-border bg-canvas2 px-2.5 py-2 text-sm font-semibold text-ink outline-none transition-colors focus:border-ink/40"
                >
                  {NAVBAR_CLOCK_SIZE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">Font</span>
                <select
                  aria-label="Clock font"
                  value={clockPreferences.font}
                  onChange={event => handleClockPreference('font', event.target.value)}
                  className="mt-1.5 w-full rounded-md border border-border bg-canvas2 px-2.5 py-2 text-sm font-semibold text-ink outline-none transition-colors focus:border-ink/40"
                >
                  {NAVBAR_CLOCK_FONT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {CLOCK_COLOR_MODES.map(mode => {
                const themeInk = getThemeBuilderValues(mode.id).ink
                return (
                  <div key={mode.id} className="rounded-lg border border-border bg-canvas2/40 px-3 py-3">
                    <div className="text-sm font-semibold text-ink">{mode.label}</div>
                    <div className="mt-2 flex items-end gap-2">
                      <label className="min-w-0 flex-1">
                        <span className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">Clock Color</span>
                        <input
                          aria-label={`${mode.ariaPrefix} clock color hex`}
                          value={clockColorDrafts[mode.id]}
                          onChange={event => handleClockColorDraft(mode.id, event.target.value)}
                          onBlur={() => handleClockColorBlur(mode.id)}
                          placeholder={themeInk}
                          maxLength={7}
                          spellCheck="false"
                          className="mt-1.5 w-full rounded-md border border-border bg-canvas px-2.5 py-2 font-mono text-xs uppercase text-ink outline-none transition-colors placeholder:text-inkMuted focus:border-ink/40"
                        />
                      </label>
                      <input
                        type="color"
                        aria-label={`${mode.ariaPrefix} clock color`}
                        value={clockPreferences.colors[mode.id] || themeInk}
                        onChange={event => handleClockColor(mode.id, event.target.value)}
                        className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-border bg-canvas p-1"
                      />
                    </div>
                    <div className="mt-2 font-mono text-[10px] text-inkMuted">
                      Leave blank to follow this theme.
                    </div>
                  </div>
                )
              })}
            </div>

            <ActionSuccessToast message={clockAppearanceMsg?.type === 'success' ? clockAppearanceMsg.text : null} onDismiss={() => setClockAppearanceMsg(null)} />
            {clockAppearanceMsg && clockAppearanceMsg.type !== 'success' && (
              <div className={`font-mono text-xs ${clockAppearanceMsg.type === 'success' ? 'text-forest' : 'text-ember'}`}>
                {clockAppearanceMsg.text}
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <Palette size={15} className="text-inkMuted" />
                <div>
                  <div className="text-sm font-semibold text-ink">Themes</div>
                  <div className="font-mono text-xs text-inkMuted mt-0.5">
                    {themeSyncing ? 'Syncing Supabase' : `${activeThemeLabel} templates`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleThemePaletteCreate}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-canvas2 px-3 py-2 text-xs font-semibold text-ink hover:border-ink/30 transition-colors"
              >
                <Plus size={13} />
                New Theme
              </button>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handleThemePaletteLoad('')}
                className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  !selectedThemePaletteId
                    ? 'border-forest/50 bg-forest/10'
                    : 'border-border bg-canvas2/35 hover:border-ink/30'
                }`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink truncate">Custom Theme</span>
                  <span className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted">
                    {selectedThemePaletteId ? 'Manual colors' : 'Active'}
                  </span>
                </span>
                {!selectedThemePaletteId && (
                  <span className="rounded-md border border-forest/50 bg-forest/15 px-2 py-1 font-mono text-[10px] text-forest">Active</span>
                )}
              </button>

              {themePalettes.map(palette => {
                const active = selectedThemePaletteId === palette.id
                return (
                  <div key={palette.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 ${
                    active ? 'border-forest/50 bg-forest/10' : 'border-border bg-canvas2/35'
                  }`}>
                    <button
                      type="button"
                      onClick={() => handleThemePaletteLoad(palette.id)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span className="block text-sm font-semibold text-ink truncate">{palette.name}</span>
                      <span className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted">
                        {active ? 'Active' : `${activeThemeLabel} theme`}
                      </span>
                    </button>
                    {active && (
                      <span className="hidden sm:inline-flex rounded-md border border-forest/50 bg-forest/15 px-2 py-1 font-mono text-[10px] text-forest">Active</span>
                    )}
                    <button
                      type="button"
                      aria-label={`Edit ${palette.name}`}
                      onClick={() => handleThemePaletteEdit(palette.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-canvas text-inkMuted hover:border-ink/40 hover:text-ink transition-colors"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${palette.name}`}
                      onClick={() => handleThemePaletteDelete(palette.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-canvas text-inkMuted hover:border-ember/50 hover:text-ember transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )
              })}

              {themePalettes.length === 0 && (
                <div className="rounded-lg border border-border bg-canvas2/35 px-3 py-3 font-mono text-xs text-inkMuted">
                  No saved themes
                </div>
              )}
            </div>

            {themeBuilderOpen && (
              <div className="rounded-lg border border-border bg-canvas2/35 p-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-ink">
                      {editingThemePaletteId ? 'Edit Theme' : 'Create Theme'}
                    </div>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">{activeThemeLabel}</div>
                  </div>
                  <button
                    type="button"
                    aria-label="Close theme builder"
                    onClick={() => setThemeBuilderOpen(false)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-canvas text-inkMuted hover:border-ink/40 hover:text-ink transition-colors"
                  >
                    <X size={13} />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-2">
                  <input
                    aria-label="Theme name"
                    value={themePaletteName}
                    onChange={e => setThemePaletteName(e.target.value)}
                    placeholder="Theme name"
                    maxLength={60}
                    className="w-full rounded-md border border-border bg-canvas px-2 py-2 text-sm text-ink placeholder:text-inkMuted focus:outline-none focus:border-ink/40"
                  />
                  <button
                    type="button"
                    onClick={handleThemePaletteSave}
                    disabled={themeSyncing}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-forest/50 bg-forest/15 px-3 py-2 text-xs font-semibold text-forest hover:border-forest transition-colors disabled:opacity-50"
                  >
                    <Save size={13} />
                    Save Theme
                  </button>
                  <button
                    type="button"
                    onClick={handleThemeBuilderReset}
                    disabled={themeSyncing}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-canvas px-3 py-2 text-xs font-semibold text-ink hover:border-ink/30 transition-colors disabled:opacity-50"
                  >
                    <RotateCcw size={13} />
                    Reset
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {THEME_BUILDER_TOKENS.map(token => {
                    const value = themeBuilderColors[token.key]
                    const draftValue = themeBuilderDrafts[token.key] ?? value
                    return (
                      <div key={token.key} className="rounded-lg border border-border bg-canvas/60 px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-ink truncate">{token.label}</div>
                            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">{token.role}</div>
                          </div>
                          <input
                            type="color"
                            aria-label={`${token.label} color`}
                            value={value}
                            onChange={e => handleThemeBuilderColor(token.key, e.target.value)}
                            className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-border bg-canvas p-1"
                          />
                        </div>
                        <input
                          aria-label={`${token.label} hex`}
                          value={draftValue}
                          onChange={e => handleThemeBuilderHexInput(token.key, e.target.value)}
                          onBlur={() => handleThemeBuilderHexBlur(token.key)}
                          className="mt-2 w-full rounded-md border border-border bg-canvas px-2 py-1.5 font-mono text-xs uppercase text-ink focus:outline-none focus:border-ink/40"
                          spellCheck="false"
                          inputMode="text"
                          maxLength={7}
                        />
                      </div>
                    )
                  })}
                </div>

                <div className="rounded-lg border border-border bg-canvas/60 p-3">
                  <div className="grid grid-cols-[1fr_auto] gap-3">
                    <div className="rounded-lg border border-border bg-canvas p-3">
                      <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">Preview</div>
                      <div className="mt-1 text-sm font-black uppercase tracking-wide text-ink">Erevis Terminal</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-md border border-border bg-canvas2 px-2 py-1 font-mono text-[10px] text-inkMuted">Panel</span>
                        <span className="rounded-md border border-forest/50 bg-forest/15 px-2 py-1 font-mono text-[10px] text-forest">Profit</span>
                        <span className="rounded-md border border-ember/50 bg-ember/15 px-2 py-1 font-mono text-[10px] text-ember">Risk</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 self-stretch">
                      {THEME_BUILDER_TOKENS.slice(0, 9).map(token => (
                        <div
                          key={token.key}
                          aria-label={`${token.label} swatch`}
                          className="h-full min-h-8 w-8 rounded-md border border-border"
                          style={{ backgroundColor: themeBuilderColors[token.key] }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <ActionSuccessToast message={themeBuilderMsg?.type === 'success' ? themeBuilderMsg.text : null} onDismiss={() => setThemeBuilderMsg(null)} />
            {themeBuilderMsg && themeBuilderMsg.type !== 'success' && (
              <div className={`font-mono text-xs ${themeBuilderMsg.type === 'success' ? 'text-forest' : 'text-ember'}`}>
                {themeBuilderMsg.text}
              </div>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      {/* ── Section 1: Profile ────────────────────────────────────────── */}
      {activeSettingsArea === 'account' && (
        <>
      <div data-testid="account-settings-grid" className="grid gap-4 xl:grid-cols-2 xl:items-start">
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Profile</h2>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div>
            <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Email</label>
            <div className="font-mono text-xs text-ink bg-canvas2 border border-border rounded-md px-2.5 py-1.5">{user?.email}</div>
          </div>
          <div>
            <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className={inputCls}
            />
          </div>
          <ActionSuccessToast message={pwMsg?.type === 'success' ? pwMsg.text : null} onDismiss={() => setPwMsg(null)} />
          {pwMsg && pwMsg.type !== 'success' && (
            <div className={`font-mono text-xs ${pwMsg.type === 'success' ? 'text-forest' : 'text-ember'}`}>
              {pwMsg.text}
            </div>
          )}
          <button
            onClick={handleChangePassword}
            disabled={!newPassword}
            className="btn-primary text-xs disabled:opacity-40"
          >
            Update Password
          </button>
        </div>
      </div>

      {/* ── Section 1B: Passkeys ─────────────────────────────────────── */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Fingerprint size={14} className="text-inkMuted" />
            <h2 className="font-black text-sm uppercase tracking-wide text-ink">Passkeys</h2>
          </div>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border bg-canvas2/40 px-3 py-2.5">
            <div>
              <div className="text-sm font-semibold text-ink">WebAuthn Login</div>
              <div className="font-mono text-xs text-inkMuted mt-0.5">
                {loginWebAuthnEnabled ? 'Passkey verification is required after password sign-in' : 'Email and password only'}
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleLoginWebAuthn}
              disabled={settingsLoading}
              aria-pressed={loginWebAuthnEnabled}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${
                loginWebAuthnEnabled ? 'bg-forest' : 'bg-border'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                loginWebAuthnEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <ActionSuccessToast message={loginAuthMsg?.type === 'success' ? loginAuthMsg.text : null} onDismiss={() => setLoginAuthMsg(null)} />
          {loginAuthMsg && loginAuthMsg.type !== 'success' && (
            <div className={`font-mono text-xs ${loginAuthMsg.type === 'success' ? 'text-forest' : 'text-ember'}`}>
              {loginAuthMsg.text}
            </div>
          )}

          {passkeysLoading ? (
            <div className="font-mono text-xs text-inkMuted animate-pulse">Loading...</div>
          ) : passkeys.length === 0 ? (
            <p className="font-mono text-xs text-inkMuted leading-relaxed">
              Add a passkey to sign in with Touch ID, Face ID, or Windows Hello instead of a password.
            </p>
          ) : (
            <div className="max-h-[16.25rem] space-y-1.5 overflow-y-auto pr-1">
              {passkeys.map(pk => (
                <div key={pk.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-canvas2 border border-border">
                  <div>
                    <div className="text-xs font-semibold text-ink">{pk.device_name || 'Unnamed Device'}</div>
                    <div className="font-mono text-[10px] text-inkMuted">
                      Added {new Date(pk.created_at).toLocaleDateString()}
                      {pk.last_used_at && ` · Last used ${new Date(pk.last_used_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeletePasskey(pk.id)}
                    className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors ${
                      deleteConfirm === pk.id
                        ? 'border-ember/60 bg-ember/10 text-ember'
                        : 'border-border text-inkMuted hover:border-ember/40 hover:text-ember'
                    }`}
                  >
                    <X size={11} />
                    {deleteConfirm === pk.id ? 'Confirm Remove' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          )}

          <ActionSuccessToast message={passkeyMsg?.type === 'success' ? passkeyMsg.text : null} onDismiss={() => setPasskeyMsg(null)} />
          {passkeyMsg && passkeyMsg.type !== 'success' && (
            <div className={`font-mono text-xs ${passkeyMsg.type === 'success' ? 'text-forest' : 'text-ember'}`}>
              {passkeyMsg.text}
            </div>
          )}

          <button
            onClick={handleRegisterPasskey}
            disabled={registerLoading}
            className="flex items-center gap-2 btn-primary text-xs disabled:opacity-40"
          >
            <Plus size={12} />
            {registerLoading ? 'Registering...' : 'Register New Passkey'}
          </button>
        </div>
      </div>
      </div>
        </>
      )}

      {/* ── Section 2B: Timezone ────────────────────────────────────── */}
      {activeSettingsArea === 'workspace' && (
        <>
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Timezone</h2>
        </div>
        <div className="px-4 py-3">
          <div>
            <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">Display Timezone</label>
            <select
              value={settings.timezone || 'America/Chicago'}
              onChange={async (e) => {
                await updateSettings({ timezone: e.target.value })
              }}
              className={inputCls}
            >
              <option value="America/New_York">Eastern (ET)</option>
              <option value="America/Chicago">Central (CT)</option>
              <option value="America/Denver">Mountain (MT)</option>
              <option value="America/Los_Angeles">Pacific (PT)</option>
            </select>
            <div className="font-mono text-[10px] text-inkMuted mt-1.5">
              Used for chart times, trade logs, and execution timestamps
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2B-bis: Trading Mode (PT-01) ──────────────────────── */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Trading Mode</h2>
          <div className="font-mono text-[10px] text-inkMuted mt-0.5">
            Live = real Schwab account data. Paper = IBKR paper account (DUM666423). Flipping this changes which broker connection the app is configured against.
          </div>
        </div>
        <div className="px-4 py-3">
          <div role="group" aria-label="Trading mode" className="inline-flex rounded-lg border border-border overflow-hidden font-mono text-[11px] tracking-widest uppercase">
            {['live', 'paper'].map((opt) => {
              const active = mode === opt
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={modeBusy}
                  onClick={() => handleModeChange(opt)}
                  aria-pressed={active}
                  className={`px-4 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                    active
                      ? (opt === 'paper'
                          ? 'bg-[#4a90e2]/15 text-[#4a90e2]'
                          : 'bg-canvas2 text-ink')
                      : 'text-inkMuted hover:text-ink'
                  } ${opt === 'paper' ? 'border-l border-border' : ''}`}
                >
                  {opt}
                </button>
              )
            })}
          </div>
          {modeMsg && (
            <div className={`mt-3 font-mono text-[10px] ${modeMsg.type === 'error' ? 'text-ember' : 'text-inkMuted'}`}>
              {modeMsg.text}
            </div>
          )}
        </div>
      </div>

      {/* ── Section 2C: Default Page ──────────────────────────────────── */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Default Page</h2>
        </div>
        <div className="px-4 py-3">
          <div>
            <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">Page After Login / Refresh</label>
            <select
              value={settings.default_page || 'last_visited'}
              onChange={async (e) => {
                await updateSettings({ default_page: e.target.value })
              }}
              className={inputCls}
            >
              <option value="last_visited">Last Visited</option>
              <option value="/dashboard">Dashboard</option>
              <option value="/positions/board">Position Board</option>
              <option value="/positions/history">Trade Log</option>
              <option value="/analytics">Analytics</option>
              <option value="/accounts/summary">Accounts Summary</option>
              <option value="/positions/planner">Position Sizing</option>
            </select>
            <div className="font-mono text-[10px] text-inkMuted mt-1.5">
              "Last Visited" remembers where you left off before refresh
            </div>
          </div>
        </div>
      </div>

      <CommandSettings
        initialPageId={searchParams.get('commandPage') || 'all'}
        initialCommandId={searchParams.get('command')}
      />
        </>
      )}

      {/* ── Section 3: Trading Defaults ───────────────────────────────── */}
      {activeSettingsArea === 'trading' && (
        <>
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Trading Defaults</h2>
          <div className="font-mono text-[10px] text-inkMuted mt-0.5">System-wide defaults for position sizing</div>
        </div>
        <div className="px-4 py-3 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">Allocation %</label>
              <input
                type="number"
                value={allocPct}
                onChange={e => setAllocPct(e.target.value)}
                step="0.5"
                min="0"
                max="100"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">Risk %</label>
              <input
                type="number"
                value={riskPct}
                onChange={e => setRiskPct(e.target.value)}
                step="0.5"
                min="0"
                max="100"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">Tranches</label>
              <input
                type="number"
                value={tranches}
                onChange={e => setTranches(e.target.value)}
                step="1"
                min="1"
                max="10"
                className={inputCls}
              />
            </div>
          </div>
          <ActionSuccessToast message={settingsMsg?.type === 'success' ? settingsMsg.text : null} onDismiss={() => setSettingsMsg(null)} />
          {settingsMsg && settingsMsg.type !== 'success' && (
            <div className={`font-mono text-xs ${settingsMsg.type === 'success' ? 'text-forest' : 'text-ember'}`}>
              {settingsMsg.text}
            </div>
          )}
          <button onClick={handleSaveDefaults} className="btn-primary text-xs">
            Save Defaults
          </button>
        </div>
      </div>

      {/* ── Section 3B: Order Management ───────────────────────────── */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Order Management</h2>
          <div className="font-mono text-[10px] text-inkMuted mt-0.5">Control live order execution to Schwab</div>
        </div>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-ink">Live Order Execution</div>
              <div className="font-mono text-xs text-inkMuted mt-0.5 max-w-sm">
                When enabled, Erevis can send orders directly to Schwab.
                Turn off during development or to prevent accidental orders.
              </div>
            </div>
            <button
              onClick={async () => {
                const next = !settings.oms_enabled
                // Enabling OMS requires biometric verification.
                // FA-01-passkey-hardening (D2): surface the specific
                // error so no-passkey users know to register one in
                // Settings rather than seeing the toggle silently revert.
                if (next) {
                  const { verified, error } = await biometricGate.verify()
                  if (!verified) {
                    setOmsError(error || 'Biometric verification failed')
                    return
                  }
                }
                // Clear any stale error when the toggle succeeds (or on
                // toggle-off, which has no gate). codex-v3-followup polish.
                setOmsError(null)
                await updateSettings({ oms_enabled: next })
              }}
              disabled={biometricGate.verifying}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                settings.oms_enabled ? 'bg-forest' : 'bg-border'
              } ${biometricGate.verifying ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                settings.oms_enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          <div className={`mt-3 flex items-center gap-2 font-mono text-xs ${
            omsLive ? 'text-forest' : 'text-inkMuted'
          }`}>
            <span className={`inline-block w-2 h-2 rounded-full ${
              omsLive ? 'bg-forest animate-pulse' : 'bg-border'
            }`} />
            {omsLive ? 'LIVE \u2014 Orders will be sent to Schwab' : 'DISABLED \u2014 No orders will be sent'}
          </div>
          {omsError && (
            <div className="mt-2 font-mono text-[11px] text-red-400">{omsError}</div>
          )}
        </div>
      </div>

      {/* ── Section 3C: Order Safety Limits ───────────────────────── */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Fingerprint size={14} className="text-inkMuted" />
            <h2 className="font-black text-sm uppercase tracking-wide text-ink">Order Safety Limits</h2>
          </div>
          <div className="font-mono text-[10px] text-inkMuted mt-0.5">Requires Touch ID to change</div>
        </div>
        <div className="px-4 py-3 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">Max Quantity Per Order</label>
              <input type="number" value={maxQty} onChange={e => setMaxQty(e.target.value)} step="1" min="1" max="10000" className={inputCls} />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">Max Notional Per Order ($)</label>
              <input type="number" value={maxNotional} onChange={e => setMaxNotional(e.target.value)} step="100" min="100" max="1000000" className={inputCls} />
            </div>
          </div>
          <div className="font-mono text-[10px] text-inkMuted leading-relaxed">
            Orders exceeding these limits will be blocked by the OMS safety layer. Changing these values requires biometric verification.
          </div>
          <ActionSuccessToast message={limitsMsg?.type === 'success' ? limitsMsg.text : null} onDismiss={() => setLimitsMsg(null)} />
          {limitsMsg && limitsMsg.type !== 'success' && (
            <div className={`font-mono text-xs ${limitsMsg.type === 'success' ? 'text-forest' : 'text-ember'}`}>{limitsMsg.text}</div>
          )}
          <button onClick={handleSaveLimits} disabled={biometricGate.verifying} className="btn-primary text-xs disabled:opacity-40">
            {biometricGate.verifying ? 'Verifying…' : 'Save Limits'}
          </button>
        </div>
      </div>
        </>
      )}

      {/* ── Section 4: Integrations ─────────────────────────────────── */}
      {activeSettingsArea === 'broker' && (
        <>

      <ActionSuccessToast message={integrationsBanner?.type === 'success' ? integrationsBanner.message : null} onDismiss={() => setIntegrationsBanner(null)} />
      {integrationsBanner && integrationsBanner.type !== 'success' && (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-lg text-sm font-semibold border ${
          integrationsBanner.type === 'success' ? 'bg-forest/10 border-forest/30 text-forest' :
          integrationsBanner.type === 'warning' ? 'bg-ember/10 border-ember/30 text-ink' :
          'bg-ember/10 border-ember/30 text-ember'
        }`}>
          {integrationsBanner.type === 'success' && <CheckCircle size={16} className="mt-0.5 shrink-0" />}
          {integrationsBanner.type === 'warning' && <AlertCircle size={16} className="mt-0.5 shrink-0" />}
          {integrationsBanner.type === 'error' && <XCircle size={16} className="mt-0.5 shrink-0" />}
          <span>{integrationsBanner.message}</span>
          <button onClick={() => setIntegrationsBanner(null)} className="ml-auto opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-black text-sm uppercase tracking-wide text-ink">Integrations</h2>
          <div className="font-mono text-[10px] text-inkMuted mt-0.5">Brokerage connections and data sync</div>
        </div>
        <div className="px-4 py-3 space-y-4">
          {syncLoading ? (
            <div className="font-mono text-xs text-inkMuted animate-pulse">Loading...</div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${tokenRow ? 'bg-forest/10' : 'bg-canvas2'}`}>
                    <Plug size={18} className={tokenRow ? 'text-forest' : 'text-inkMuted'} strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-black text-sm uppercase tracking-wide text-ink">Charles Schwab</div>
                    <div className="font-mono text-[11px] text-inkMuted mt-0.5">
                      {tokenRow ? (
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-forest" />
                          Connected{tokenAge?.lastSync ? ` · Last synced ${tokenAge.lastSync}` : ''}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-border" />
                          Not connected
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {tokenRow ? (
                    <>
                      <button onClick={handleSyncNow} disabled={integrationsSyncing} className="btn-secondary flex items-center gap-2 text-xs">
                        <RefreshCw size={13} className={integrationsSyncing ? 'animate-spin' : ''} strokeWidth={2} />
                        {integrationsSyncing ? 'Syncing…' : 'Sync Now'}
                      </button>
                      <button onClick={handleDisconnect} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-ember/40 text-ember hover:bg-ember/10 transition-colors">
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button onClick={handleConnect} className="btn-primary flex items-center gap-2 text-xs">
                      <Plug size={13} strokeWidth={2} />
                      Connect Schwab
                    </button>
                  )}
                </div>
              </div>

              {tokenRow && tokenAge && (
                <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border font-mono text-xs ${
                  tokenAge.isExpired ? 'border-ember/40 bg-ember/10 text-ember'
                    : tokenAge.isWarning ? 'border-[#c4953a]/40 bg-[#c4953a]/10 text-[#c4953a]'
                    : 'border-border bg-canvas2/50 text-inkMuted'
                }`}>
                  <AlertCircle size={14} className="shrink-0" />
                  <div>
                    <span className="font-semibold">
                      {tokenAge.isExpired ? 'Schwab authentication expired'
                        : tokenAge.isWarning ? `Schwab auth expires ${tokenAge.daysRemaining === 1 ? 'tomorrow' : `in ${tokenAge.daysRemaining} days`}`
                        : `Schwab auth: ${tokenAge.daysRemaining} days remaining`}
                    </span>
                    <span className="text-inkMuted ml-2">
                      Authenticated <PrivateValue>{tokenAge.authDate}</PrivateValue> · Tokens expire after 7 days
                    </span>
                  </div>
                  {(tokenAge.isExpired || tokenAge.isWarning) && (
                    <button onClick={handleConnect} className="ml-auto shrink-0 btn-primary text-[10px] px-3 py-1">Reconnect</button>
                  )}
                </div>
              )}

              {tokenRow && accounts.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-canvas2 border-b border-border">
                        <th className="text-left px-4 py-2.5 font-semibold text-inkMuted text-xs uppercase tracking-wide">Account</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-inkMuted text-xs uppercase tracking-wide">Type</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-inkMuted text-xs uppercase tracking-wide">Equity</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-inkMuted text-xs uppercase tracking-wide">Positions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((acct, i) => {
                        const eq = acct.equity || {}
                        return (
                          <tr key={acct.account_number} className={`border-b border-border last:border-0 ${i % 2 !== 0 ? 'bg-canvas2/30' : ''}`}>
                            <td className="px-4 py-3"><AccountName accountNumber={acct.account_number} className="font-mono text-xs text-ink font-semibold" /></td>
                            <td className="px-4 py-3 font-mono text-xs text-inkMuted">{acct.account_type || '—'}</td>
                            <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-ink"><PrivateValue>{fmtEq(eq.liquidationValue ?? eq.totalAccountValue)}</PrivateValue></td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{positionCounts[acct.account_number] ?? 0}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {tokenRow && accounts.length === 0 && (
                <div className="text-center py-4 text-sm text-inkMuted font-mono">
                  No account data yet.{' '}<button onClick={handleSyncNow} className="text-ink underline">Sync now</button>
                </div>
              )}

              {!tokenRow && (
                <p className="text-sm text-inkMuted leading-relaxed">
                  Connect your Charles Schwab account to sync positions, equity, and trade history.
                </p>
              )}

              <Link to="/import" className="inline-flex items-center gap-1.5 font-mono text-xs text-inkMuted hover:text-ink transition-colors">
                Import CSV data <ExternalLink size={10} />
              </Link>
            </>
          )}
        </div>
      </div>
        </>
      )}

      {/* ── Section 5: Danger Zone ────────────────────────────────────── */}
      {activeSettingsArea === 'account' && (
      <div className="bg-canvas border border-ember/30 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-ember/30">
          <h2 className="font-black text-sm uppercase tracking-wide text-ember">Danger Zone</h2>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-ink">Clear Local Data</div>
              <div className="font-mono text-xs text-inkMuted">Reset theme preference and other local settings</div>
            </div>
            <button
              onClick={handleClearLocal}
              className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border border-ember/40 text-ember hover:bg-ember/10 transition-colors"
            >
              <Trash2 size={12} />
              Clear
            </button>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div>
              <div className="text-sm font-semibold text-ink">Sign Out</div>
              <div className="font-mono text-xs text-inkMuted">End your current session</div>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border border-ember/40 text-ember hover:bg-ember/10 transition-colors"
            >
              <LogOut size={12} />
              Sign Out
            </button>
          </div>
        </div>
      </div>
      )}
          </div>
        </main>
      </div>
    </div>
  )
}
